import React, { useEffect } from 'react';
import logger from '../utils/logger';

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  options: { value: string; label: string }[];
}

const Select: React.FC<SelectProps> = ({ 
  label, 
  error, 
  options,
  className = '',
  onChange,
  onFocus,
  onBlur,
  name,
  value,
  ...props 
}) => {
  useEffect(() => {
    logger.debug('Select', 'Select rendered', { name, label, optionsCount: options.length, hasError: !!error, currentValue: value });
  }, [name, label, options.length, error, value]);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    logger.userAction('Select', 'Select value changed', { name, oldValue: value, newValue: e.target.value });
    if (onChange) {
      onChange(e);
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLSelectElement>) => {
    logger.debug('Select', 'Select focused', { name });
    if (onFocus) {
      onFocus(e);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLSelectElement>) => {
    logger.debug('Select', 'Select blurred', { name, value: e.target.value });
    if (onBlur) {
      onBlur(e);
    }
  };

  return (
    <div className="w-full relative" style={{ zIndex: 1 }}>
      {label && (
        <label className="block text-sm sm:text-base font-medium text-text-secondary mb-2.5 font-elegant tracking-luxury-tight">
          {label}
        </label>
      )}
      <div className="relative" style={{ isolation: 'isolate' }}>
      <select
        className={`
          w-full px-4 py-3 rounded-xl
          glass-grain backdrop-blur-xl
          border ${error ? 'border-red-500/50' : 'border-cosmic-gold/30'}
          text-text-primary
          font-elegant text-base tracking-luxury-tight
          focus:outline-none focus:border-cosmic-gold/60 focus:ring-0
          transition-all duration-300
          cursor-pointer
            appearance-none
          ${className}
        `}
        style={{
          backgroundColor: error ? 'rgba(20, 20, 32, 0.3)' : 'rgba(20, 20, 32, 0.3)',
            boxShadow: error ? '0 0 20px rgba(239, 68, 68, 0.2), 0 8px 32px rgba(0,0,0,0.4)' : '0 8px 32px rgba(0,0,0,0.4)',
            position: 'relative',
            zIndex: 10
        }}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        name={name}
        value={value}
        {...props}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value} style={{ backgroundColor: '#141420' }}>
            {option.label}
          </option>
        ))}
      </select>
        {/* Custom dropdown arrow */}
        <div 
          className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none"
          style={{ zIndex: 11 }}
        >
          <svg 
            className="w-5 h-5 text-cosmic-gold/60" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
      {error && (
        <p className="mt-2 text-sm text-red-400 font-elegant tracking-luxury-tight">{error}</p>
      )}
    </div>
  );
};

export default Select;

